/**
 * @file submission.js
 * Handles form validation and submission logic.
 */

import { generateBookingCode } from "./utils.js";
import { thaiMonths } from "./zodiac.js";
import { initBirthdateForm } from "./birthdate.js";
import { initAppointmentForm, APPOINTMENT_CONFIG } from "./appointment.js";
import timezoneData from "../timezoneData.json";

const form = document.getElementById("bookingForm");
const nameInput = document.querySelector("input[name='name']");
const hiddenDate = document.getElementById("hidden-date");
const timepicker = document.getElementById("timepicker");

/**
 * Resolves a friendly display name for a given timezone.
 * @param {string} tz - IANA timezone identifier.
 * @returns {string} Friendly location name.
 */
function getTimezoneName(tz) {
  if (!tz) return "-";
  if (tz === "Asia/Bangkok") {
    return "ไทย";
  }
  if (tz === "America/Los_Angeles") {
    return "Washington";
  }
  if (tz === "America/Chicago") {
    return "Texas";
  }
  if (tz === "America/New_York") {
    return "New York";
  }
  if (tz === "America/Denver") {
    return "Denver";
  }
  if (tz === "America/Phoenix") {
    return "Arizona";
  }
  const country = timezoneData?.COUNTRIES?.find((c) => c.tz === tz);
  if (country) {
    return country.name;
  }
  const state = timezoneData?.US_STATES?.find((s) => s.tz === tz);
  if (state) {
    return state.name;
  }
  return tz.includes("/") ? tz.split("/").pop().replace(/_/g, " ") : tz;
}

/**
 * Formats dates for submission and converts to the configured host timezone.
 */
function formatDatesForSubmission(dateVal, timeVal) {
  const [hours, minutes] = timeVal.split(":").map(Number);
  const userZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Bangkok";
  const hostZone = APPOINTMENT_CONFIG?.timeZone || "America/Los_Angeles";

  const dt = luxon.DateTime.fromISO(dateVal, { zone: userZone }).set({
    hour: hours,
    minute: minutes,
  });

  const formattedDate = `${dt.day} ${thaiMonths[dt.month]} ${dt.year + 543}`;
  const formattedTime = dt.toFormat("HH:mm");

  const hostDT = dt.setZone(hostZone);
  const hostFormatted = `${hostDT.day} ${thaiMonths[hostDT.month]} ${
    hostDT.year + 543
  } เวลา ${hostDT.toFormat("HH:mm")} น.`;

  return { formattedDate, formattedTime, hostFormatted, userZone, hostZone };
}

/**
 * Handles a successful form submission.
 */
export function handleFormSuccess(bookingCode) {
  Swal.fire({
    icon: "success",
    title: "<b>ขอบคุณสำหรับการจองคิว</b>",
    html: `
      <p>กรุณาจดรหัสการจองไว้: <strong style="color: red;">${bookingCode}</strong></p>
      <p>ครูพลอยจะคอนเฟิร์มภายใน <b>12 ชม</b> ผ่านทางไลน์</p>
      <p>
        โปรดแอดไลน์ ที่ ID Line:
        <span class="text-color-main"><strong>ploy.tip</strong></span>
      </p>
      <p>
        หรือกดที่นี่
        <a rel="noreferrer" href="https://line.me/ti/p/hJdG41iKRb" target="_blank">
          <i class="fab fa-line fa-2x"></i>
        </a>
      </p>
      <p><strong>ในระหว่างนี้ โปรดอ่านข้อมูลเพิ่มเติมด้านล่าง</strong></p>
      <p>
        <a href="steps.html" class="cta-btn cta-btn--hero" target="_blank">
          ขั้นตอนการดูไพ่ยิปซีออนไลน์
        </a>
      </p>
    `,
    showConfirmButton: false,
    showCloseButton: true
  });

  if (form) {
    form.reset();
  }

  ["hidden-date", "hidden_birth_date", "chinese_zodiac"].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.value = "";
  });

  // Re-initialize the forms to reset their state
  initBirthdateForm();
  initAppointmentForm();
}

/**
 * Handles a form submission error.
 */
function handleFormError() {
  Swal.fire({
    icon: "error",
    title: "ไม่สามารถส่งฟอร์มได้",
    html: `
    <p>ขออภัยในความไม่สะดวก ระบบมีปัญหาในการส่งฟอร์มของคุณ
       กรุณาแอดไลน์เพื่อทำการจองคิว ที่ <br/> ID Line:
      <span class="text-color-main"><strong>ploy.tip</strong></span>
      <br/>หรือกดที่นี่
      <a rel="noreferrer" href="https://line.me/ti/p/hJdG41iKRb" target="_blank">
        <i class="fab fa-line fa-2x"></i>
      </a>
    </p>
    <h2>ขอบคุณค่ะ</h2>
  `,
  confirmButtonText: 'ตกลง'
  });
}

/**
 * Initializes the form submission handler.
 */
export function initFormSubmission() {
  // Ensure DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFormSubmission);
    return;
  }
  const form = document.getElementById("bookingForm");

  if (!form) {
    console.warn('Form with ID "bookingForm" not found');
    return;
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    // Get fresh references to elements
    const hiddenDate = document.getElementById("hidden-date");
    const timepicker = document.getElementById("timepicker");
    const nameInput = document.querySelector("input[name='name']");

    // Add null checks for required elements
    if (!hiddenDate) {
      console.error("Hidden date element not found");
      return;
    }

    if (!timepicker) {
      console.error("Timepicker element not found");
      return;
    }

    if (!nameInput) {
      console.error("Name input element not found");
      return;
    }

    const dateVal = hiddenDate.value;
    const timeVal = timepicker.value;
    const name = nameInput.value.trim();

    const bookingCode = generateBookingCode();

    // Add null checks for form elements
    const bookingCodeElement = document.getElementById("booking_code");
    const subjectElement = document.getElementById("subject");
    const texasDatetimeElement = document.getElementById("texas_datetime");

    if (bookingCodeElement) {
      bookingCodeElement.value = bookingCode;
    }

    if (subjectElement) {
      subjectElement.value = `จองคิว - ${name} - ${bookingCode}`;
    }

    const { formattedDate, formattedTime, hostFormatted, userZone, hostZone } =
      formatDatesForSubmission(dateVal, timeVal);
    hiddenDate.value = formattedDate;

    if (texasDatetimeElement) {
      texasDatetimeElement.value = hostFormatted;
    }

    const serviceSelect = form.querySelector("select[name='service']");
    const service = serviceSelect?.value || "-";
    const birthDateInput = document.getElementById("hidden_birth_date");
    const birthDate = birthDateInput?.value || "-";
    const zodiacInput = document.getElementById("chinese_zodiac");
    const zodiac = zodiacInput?.value || "-";

    const isSameZone = userZone === hostZone;
    const userTzName = getTimezoneName(userZone);
    const hostTzName = getTimezoneName(hostZone);

    const timeLines = isSameZone
      ? [`จองวันที่ ${formattedDate} เวลา ${formattedTime} น. ในเขตเวลาของ ${hostTzName}`]
      : [
          `จองวันที่ ${formattedDate} เวลา ${formattedTime} น. ในเขตเวลาของ ${userTzName}`,
          `ซึ่งเป็นวันที่ ${hostFormatted} ในเขตเวลาของ ${hostTzName}`,
        ];

    const messageText = [
      `วันนี้มีคนชื่อ ${name}`,
      ...timeLines,
      `ค่าครู ${service}`,
      `เกิดวันที่ ${birthDate}`,
      `ปีนักษัตร ${zodiac}`,
    ].join("\n");

    const submissionData = new FormData();
    submissionData.append("_captcha", "false");
    submissionData.append("_subject", `จองคิว - ${name} - ${bookingCode}`);
    submissionData.append("ข้อความ", messageText);
    submissionData.append("รหัสการจอง", bookingCode);

    // Try multiple selectors to find the submit button after build
    let submitButton =
      form.querySelector('button[type="submit"]') ||
      form.querySelector('input[type="submit"]') ||
      form.querySelector("button:last-of-type") ||
      form.querySelector('[type="submit"]') ||
      form.querySelector("button");

    if (submitButton) {
      // Store original text for restoration
      const originalText = submitButton.textContent;

      submitButton.disabled = true;
      submitButton.textContent = "กำลังส่ง...";

      // Use setTimeout to let the UI update complete before making the request
      setTimeout(async () => {
        try {
          const response = await fetch(form.action, {
            method: "POST",
            body: submissionData,
            headers: { Accept: "application/json" },
          });
          if (response.ok) {
            handleFormSuccess(bookingCode);
          } else {
            handleFormError();
          }
        } catch (error) {
          handleFormError();
        } finally {
          // Restore button state
          submitButton.disabled = false;
          submitButton.textContent = "จองคิว";
        }
      }, 50); // 50ms delay to ensure UI renders

      return; // Exit the main function since handling async in setTimeout
    }

    // Fallback if no submit button found
    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: submissionData,
        headers: { Accept: "application/json" },
      });
      if (response.ok) {
        handleFormSuccess(bookingCode);
      } else {
        handleFormError();
      }
    } catch (error) {
      handleFormError();
    }
  });
}

/**
 * Initializes the pop-up event handlers.
 */
export function initPopupHandlers() {}
